import { makeApi, Zodios, type ZodiosOptions } from "@zodios/core";
import { z } from "zod";

const uploadedMediaAuth_Body = z
  .object({ mediaURL: z.string().nullable(), usdoID: z.number().nullable() })
  .partial();
const authPasswordRecover_Body = z.object({
  AccessModel: z.string().nullish(),
  applID: z.string(),
  email: z.string(),
  resetURL: z.string().nullish(),
});
const authPasswordReset_Body = z.object({
  password: z.string(),
  verification: z.string(),
});
const authLogin_Body = z.object({
  applID: z.string(),
  coord: z.string().nullish(),
  deviceID: z.string(),
  deviceName: z.string(),
  email: z.string(),
  ip: z.string(),
  location: z.string(),
  password: z.string(),
  rememberMe: z.boolean(),
});
const authEmailTokenAuth_Body = z.object({
  applID: z.string(),
  email: z.string(),
});
const authEmailTokenSignin_Body = z.object({
  acveID: z.string(),
  coord: z.string().nullish(),
  deviceID: z.string(),
  deviceName: z.string(),
  ip: z.string(),
  location: z.string(),
  rememberMe: z.boolean(),
  verification: z.string(),
});
const documentCreate_Body = z.object({
  data: z.unknown(),
  description: z.string(),
  doteID: z.string(),
  formSchema: z.unknown(),
  permissionMode: z.string(),
  printSchema: z.unknown(),
  title: z.string(),
  uiSchema: z.unknown(),
});
const oauth2Consent_Body = z.object({
  AuthorizeParams: z.string(),
  Consent: z.string(),
  GrantedScopes: z.array(z.string()),
});
const authCreateAccount_Body = z.object({
  applID: z.string(),
  coord: z.string().nullish(),
  deviceID: z.string(),
  deviceName: z.string(),
  email: z.string(),
  ip: z.string(),
  location: z.string(),
  name: z.string(),
  password: z.string(),
});
const github_com_falqondev_reportdev_datalake_backend_service_GeoLocationView =
  z.object({
    address: z.object({
      cep: z.string().nullish(),
      city: z.string(),
      complement: z.string().nullish(),
      coordinate: z.string(),
      country: z.string(),
      district: z.string().nullish(),
      number: z.string().nullish(),
      state: z.string(),
      stateAcronym: z.string(),
      street: z.string().nullish(),
    }),
    addressFormatted: z.string(),
  });
const github_com_falqondev_reportdev_datalake_backend_service_PendingTerm =
  z.object({ slug: z.string(), termID: z.number(), title: z.string() });
const usersGetProfileMe_Body = z
  .object({
    avatar: z.string().nullable(),
    info: z.object({}).partial().nullable(),
    name: z.string().nullable(),
  })
  .partial();

export const schemas = {
  uploadedMediaAuth_Body,
  authPasswordRecover_Body,
  authPasswordReset_Body,
  authLogin_Body,
  authEmailTokenAuth_Body,
  authEmailTokenSignin_Body,
  documentCreate_Body,
  oauth2Consent_Body,
  authCreateAccount_Body,
  github_com_falqondev_reportdev_datalake_backend_service_GeoLocationView,
  github_com_falqondev_reportdev_datalake_backend_service_PendingTerm,
  usersGetProfileMe_Body,
};

const endpoints = makeApi([
  {
    method: "get",
    path: "/.well-known/jwks.json",
    alias: "oauth2Jwks",
    description: `JSON Web Key Set for verifying OAuth2/OIDC tokens`,
    requestFormat: "json",
    parameters: [
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z.object({
      context: z.string().nullish(),
      data: z.object({}).partial(),
    }),
  },
  {
    method: "get",
    path: "/.well-known/oauth-authorization-server",
    alias: "oauth2AuthServerDiscovery",
    description: `RFC 8414 OAuth2 Authorization Server metadata`,
    requestFormat: "json",
    parameters: [
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z.object({
      context: z.string().nullish(),
      data: z.object({}).partial(),
    }),
  },
  {
    method: "get",
    path: "/.well-known/openid-configuration",
    alias: "oauth2OidcDiscovery",
    description: `OpenID Connect Discovery`,
    requestFormat: "json",
    parameters: [
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z.object({
      context: z.string().nullish(),
      data: z.object({}).partial(),
    }),
  },
  {
    method: "post",
    path: "/api/auth-media",
    alias: "uploadedMediaAuth",
    description: `Authenticate media server access`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Request data`,
        type: "Body",
        schema: uploadedMediaAuth_Body,
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z.object({
      context: z.string().nullish(),
      data: z.object({
        item: z.object({ jwt: z.string() }),
        kind: z.string(),
        lang: z.string().nullish(),
      }),
    }),
  },
  {
    method: "get",
    path: "/api/auth/flow/:provider",
    alias: "auth3rdParty",
    description: `Login with a providers Oauth flow`,
    requestFormat: "json",
    parameters: [
      {
        name: "provider",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "applID",
        type: "Query",
        schema: z.string(),
      },
    ],
    response: z.object({
      context: z.string().nullish(),
      data: z.object({
        item: z.object({
          available2FA: z.array(z.string()),
          jwt: z.string(),
          required2FA: z.boolean(),
          roles: z.array(z.string()),
          user: z.object({
            applID: z.string(),
            auth2fa: z.object({
              methods: z.array(z.string()).nullish(),
              required: z.boolean(),
            }),
            avatar: z.string().nullish(),
            createdAt: z.string(),
            deletedAt: z.string().nullish(),
            info: z.object({}).partial(),
            name: z.string(),
            pushTokens: z.array(z.string()).nullish(),
            userID: z.string(),
          }),
        }),
        kind: z.string(),
        lang: z.string().nullish(),
      }),
    }),
  },
  {
    method: "get",
    path: "/api/auth/flow/:provider/callback",
    alias: "auth3rdPartyCallback",
    description: `Login with a providers Oauth flow`,
    requestFormat: "json",
    parameters: [
      {
        name: "provider",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "state",
        type: "Query",
        schema: z.string(),
      },
    ],
    response: z.object({
      context: z.string().nullish(),
      data: z.object({
        item: z.object({
          available2FA: z.array(z.string()),
          jwt: z.string(),
          required2FA: z.boolean(),
          roles: z.array(z.string()),
          user: z.object({
            applID: z.string(),
            auth2fa: z.object({
              methods: z.array(z.string()).nullish(),
              required: z.boolean(),
            }),
            avatar: z.string().nullish(),
            createdAt: z.string(),
            deletedAt: z.string().nullish(),
            info: z.object({}).partial(),
            name: z.string(),
            pushTokens: z.array(z.string()).nullish(),
            userID: z.string(),
          }),
        }),
        kind: z.string(),
        lang: z.string().nullish(),
      }),
    }),
  },
  {
    method: "post",
    path: "/api/auth/one-time-login",
    alias: "authOneTimeLogin",
    description: `Authenticate with single use token`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Request data`,
        type: "Body",
        schema: z.object({ token: z.string() }),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z.object({
      context: z.string().nullish(),
      data: z.object({
        item: z.object({
          available2FA: z.array(z.string()),
          jwt: z.string(),
          required2FA: z.boolean(),
          roles: z.array(z.string()),
          user: z.object({
            applID: z.string(),
            auth2fa: z.object({
              methods: z.array(z.string()).nullish(),
              required: z.boolean(),
            }),
            avatar: z.string().nullish(),
            createdAt: z.string(),
            deletedAt: z.string().nullish(),
            info: z.object({}).partial(),
            name: z.string(),
            pushTokens: z.array(z.string()).nullish(),
            userID: z.string(),
          }),
        }),
        kind: z.string(),
        lang: z.string().nullish(),
      }),
    }),
  },
  {
    method: "post",
    path: "/api/auth/password-recover",
    alias: "authPasswordRecover",
    description: `Start password recovery for account with given email`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Request data`,
        type: "Body",
        schema: authPasswordRecover_Body,
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z.object({
      context: z.string().nullish(),
      data: z.object({
        item: z.object({
          acveID: z.string(),
          createdAt: z.string(),
          type: z.string(),
          userID: z.string(),
        }),
        kind: z.string(),
        lang: z.string().nullish(),
      }),
    }),
  },
  {
    method: "post",
    path: "/api/auth/password-reset/:resetID",
    alias: "authPasswordReset",
    description: `Reset password for account with given email`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Request data`,
        type: "Body",
        schema: authPasswordReset_Body,
      },
      {
        name: "resetID",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z.object({
      context: z.string().nullish(),
      data: z.object({}).partial(),
    }),
  },
  {
    method: "post",
    path: "/api/auth/password-reset/:resetID/validate",
    alias: "authPasswordResetValidate",
    description: `Verify that password reset is still valid`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Request data`,
        type: "Body",
        schema: authPasswordReset_Body,
      },
      {
        name: "resetID",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z.object({
      context: z.string().nullish(),
      data: z.object({
        item: z.object({ isValid: z.string() }),
        kind: z.string(),
        lang: z.string().nullish(),
      }),
    }),
  },
  {
    method: "get",
    path: "/api/auth/refresh",
    alias: "authRefresh",
    description: `Generate new short-lived JWT for current session user`,
    requestFormat: "json",
    parameters: [
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z.object({
      context: z.string().nullish(),
      data: z.object({
        item: z.object({
          available2FA: z.array(z.string()),
          jwt: z.string(),
          required2FA: z.boolean(),
          roles: z.array(z.string()),
          user: z.object({
            applID: z.string(),
            auth2fa: z.object({
              methods: z.array(z.string()).nullish(),
              required: z.boolean(),
            }),
            avatar: z.string().nullish(),
            createdAt: z.string(),
            deletedAt: z.string().nullish(),
            info: z.object({}).partial(),
            name: z.string(),
            pushTokens: z.array(z.string()).nullish(),
            userID: z.string(),
          }),
        }),
        kind: z.string(),
        lang: z.string().nullish(),
      }),
    }),
  },
  {
    method: "post",
    path: "/api/auth/signin",
    alias: "authLogin",
    description: `Login with email &amp; password`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Request data`,
        type: "Body",
        schema: authLogin_Body,
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z.object({
      context: z.string().nullish(),
      data: z.object({
        item: z.object({
          available2FA: z.array(z.string()),
          jwt: z.string(),
          required2FA: z.boolean(),
          roles: z.array(z.string()),
          user: z.object({
            applID: z.string(),
            auth2fa: z.object({
              methods: z.array(z.string()).nullish(),
              required: z.boolean(),
            }),
            avatar: z.string().nullish(),
            createdAt: z.string(),
            deletedAt: z.string().nullish(),
            info: z.object({}).partial(),
            name: z.string(),
            pushTokens: z.array(z.string()).nullish(),
            userID: z.string(),
          }),
        }),
        kind: z.string(),
        lang: z.string().nullish(),
      }),
    }),
  },
  {
    method: "post",
    path: "/api/auth/signout",
    alias: "authSignout",
    description: `Signout and forget session and refresh tokens`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Request data`,
        type: "Body",
        schema: z.object({}).partial(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z.object({
      context: z.string().nullish(),
      data: z.object({
        item: z.string(),
        kind: z.string(),
        lang: z.string().nullish(),
      }),
    }),
  },
  {
    method: "post",
    path: "/api/auth/token-auth",
    alias: "authEmailTokenAuth",
    description: `Start authentication with token for given email`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Request data`,
        type: "Body",
        schema: authEmailTokenAuth_Body,
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z.object({
      context: z.string().nullish(),
      data: z.object({
        item: z.object({
          acveID: z.string(),
          createdAt: z.string(),
          type: z.string(),
          userID: z.string(),
        }),
        kind: z.string(),
        lang: z.string().nullish(),
      }),
    }),
  },
  {
    method: "post",
    path: "/api/auth/token-signin",
    alias: "authEmailTokenSignin",
    description: `Authentication with token for given email`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Request data`,
        type: "Body",
        schema: authEmailTokenSignin_Body,
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z.object({
      context: z.string().nullish(),
      data: z.object({
        item: z.object({
          available2FA: z.array(z.string()),
          jwt: z.string(),
          required2FA: z.boolean(),
          roles: z.array(z.string()),
          user: z.object({
            applID: z.string(),
            auth2fa: z.object({
              methods: z.array(z.string()).nullish(),
              required: z.boolean(),
            }),
            avatar: z.string().nullish(),
            createdAt: z.string(),
            deletedAt: z.string().nullish(),
            info: z.object({}).partial(),
            name: z.string(),
            pushTokens: z.array(z.string()).nullish(),
            userID: z.string(),
          }),
        }),
        kind: z.string(),
        lang: z.string().nullish(),
      }),
    }),
  },
  {
    method: "post",
    path: "/api/auth/validate-email",
    alias: "authValidateEmail",
    description: `checks if the email is valid for appl_id`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Request data`,
        type: "Body",
        schema: authEmailTokenAuth_Body,
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z.object({
      context: z.string().nullish(),
      data: z.object({
        item: z.object({
          applID: z.string(),
          email: z.string(),
          isValid: z.boolean(),
        }),
        kind: z.string(),
        lang: z.string().nullish(),
      }),
    }),
  },
  {
    method: "get",
    path: "/api/document-templates/:doteID",
    alias: "documentTemplateGet",
    description: `Get one document template`,
    requestFormat: "json",
    parameters: [
      {
        name: "doteID",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z.object({
      context: z.string().nullish(),
      data: z.object({
        item: z.object({
          createdAt: z.string(),
          defaultData: z.unknown(),
          deletedAt: z.string().nullish(),
          description: z.string(),
          doteID: z.string(),
          formSchema: z.unknown(),
          permissionMode: z.string(),
          printSchema: z.unknown(),
          title: z.string(),
          uiSchema: z.unknown(),
          updatedAt: z.string(),
        }),
        kind: z.string(),
        lang: z.string().nullish(),
      }),
    }),
  },
  {
    method: "post",
    path: "/api/documents",
    alias: "documentCreate",
    description: `Create one document`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Request data`,
        type: "Body",
        schema: documentCreate_Body,
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z.object({
      context: z.string().nullish(),
      data: z.object({
        item: z.object({
          createdAt: z.string(),
          data: z.unknown(),
          deletedAt: z.string().nullish(),
          description: z.string(),
          docuID: z.string(),
          doteID: z.string(),
          formSchema: z.unknown(),
          permissionMode: z.string(),
          printSchema: z.unknown(),
          title: z.string(),
          uiSchema: z.unknown(),
          updatedAt: z.string(),
        }),
        kind: z.string(),
        lang: z.string().nullish(),
      }),
    }),
  },
  {
    method: "get",
    path: "/api/oauth2/authorize",
    alias: "oauth2Authorize",
    description: `OAuth2 authorization endpoint`,
    requestFormat: "json",
    parameters: [
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z.object({
      context: z.string().nullish(),
      data: z.object({}).partial(),
    }),
  },
  {
    method: "get",
    path: "/api/oauth2/client-info",
    alias: "oauth2ClientInfo",
    description: `Get public info about an OAuth2 client for the consent screen`,
    requestFormat: "json",
    parameters: [
      {
        name: "client_id",
        type: "Query",
        schema: z.string(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z.object({
      context: z.string().nullish(),
      data: z.object({
        item: z.object({
          clientID: z.string(),
          clientName: z.string(),
          scopes: z.array(z.string()),
        }),
        kind: z.string(),
        lang: z.string().nullish(),
      }),
    }),
  },
  {
    method: "post",
    path: "/api/oauth2/consent",
    alias: "oauth2Consent",
    description: `OAuth2 consent endpoint`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Request data`,
        type: "Body",
        schema: oauth2Consent_Body,
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z.object({
      context: z.string().nullish(),
      data: z.object({}).partial(),
    }),
  },
  {
    method: "post",
    path: "/api/oauth2/introspect",
    alias: "oauth2Introspect",
    description: `OAuth2 token introspection endpoint`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Request data`,
        type: "Body",
        schema: z.object({}).partial(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z.object({
      context: z.string().nullish(),
      data: z.object({}).partial(),
    }),
  },
  {
    method: "post",
    path: "/api/oauth2/revoke",
    alias: "oauth2Revoke",
    description: `OAuth2 token revocation endpoint`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Request data`,
        type: "Body",
        schema: z.object({}).partial(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z.object({
      context: z.string().nullish(),
      data: z.object({}).partial(),
    }),
  },
  {
    method: "post",
    path: "/api/oauth2/token",
    alias: "oauth2Token",
    description: `OAuth2 token endpoint`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Request data`,
        type: "Body",
        schema: z.object({}).partial(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z.object({
      context: z.string().nullish(),
      data: z.object({}).partial(),
    }),
  },
  {
    method: "post",
    path: "/api/onboarding/create-account",
    alias: "authCreateAccount",
    description: `Create a new account`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Request data`,
        type: "Body",
        schema: authCreateAccount_Body,
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z.object({
      context: z.string().nullish(),
      data: z.object({
        item: z.object({
          available2FA: z.array(z.string()),
          jwt: z.string(),
          required2FA: z.boolean(),
          roles: z.array(z.string()),
          user: z.object({
            applID: z.string(),
            auth2fa: z.object({
              methods: z.array(z.string()).nullish(),
              required: z.boolean(),
            }),
            avatar: z.string().nullish(),
            createdAt: z.string(),
            deletedAt: z.string().nullish(),
            info: z.object({}).partial(),
            name: z.string(),
            pushTokens: z.array(z.string()).nullish(),
            userID: z.string(),
          }),
        }),
        kind: z.string(),
        lang: z.string().nullish(),
      }),
    }),
  },
  {
    method: "post",
    path: "/api/public-auth-media",
    alias: "mideaServerPublicAuth",
    description: `Public authenticate media server access`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Request data`,
        type: "Body",
        schema: z.object({}).partial(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z.object({
      context: z.string().nullish(),
      data: z.object({
        item: z.object({ jwt: z.string() }),
        kind: z.string(),
        lang: z.string().nullish(),
      }),
    }),
  },
  {
    method: "get",
    path: "/api/search/geo-locations",
    alias: "geoLocationList",
    description: `Return a search of GeoLocationView`,
    requestFormat: "json",
    parameters: [
      {
        name: "cep",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "search",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z.object({
      context: z.string().nullish(),
      data: z.object({
        currentItemCount: z.number(),
        items: z.array(
          github_com_falqondev_reportdev_datalake_backend_service_GeoLocationView
        ),
        itemsPerPage: z.number(),
        kind: z.string(),
        lang: z.string().nullish(),
        pageIndex: z.number(),
        startIndex: z.number(),
        totalItems: z.number(),
        totalPages: z.number(),
      }),
    }),
  },
  {
    method: "get",
    path: "/api/term/:slug",
    alias: "termGet",
    description: `Get one term`,
    requestFormat: "json",
    parameters: [
      {
        name: "slug",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z.object({
      context: z.string().nullish(),
      data: z.object({
        item: z.object({
          createdAt: z.string(),
          deletedAt: z.string().nullish(),
          description: z.string(),
          slug: z.string(),
          termID: z.number(),
          title: z.string(),
          type: z.string(),
        }),
        kind: z.string(),
        lang: z.string().nullish(),
      }),
    }),
  },
  {
    method: "post",
    path: "/api/term/accept",
    alias: "termAccept",
    description: `Accept a term`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Request data`,
        type: "Body",
        schema: z.object({ termID: z.number() }),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z.object({
      context: z.string().nullish(),
      data: z.object({
        item: z.object({
          accepted: z.boolean(),
          termID: z.number(),
          userID: z.string(),
        }),
        kind: z.string(),
        lang: z.string().nullish(),
      }),
    }),
  },
  {
    method: "get",
    path: "/api/term/pending",
    alias: "termGetPending",
    description: `get all pending terms`,
    requestFormat: "json",
    parameters: [
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z.object({
      context: z.string().nullish(),
      data: z.object({
        item: z.object({
          terms: z.array(
            github_com_falqondev_reportdev_datalake_backend_service_PendingTerm
          ),
          userID: z.string(),
        }),
        kind: z.string(),
        lang: z.string().nullish(),
      }),
    }),
  },
  {
    method: "get",
    path: "/api/users/:userID",
    alias: "usersGetUserByID",
    description: `Return the authenticated user&#x27;s data`,
    requestFormat: "json",
    parameters: [
      {
        name: "userID",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z.object({
      context: z.string().nullish(),
      data: z.object({
        item: z.object({
          applID: z.string(),
          auth2fa: z.object({
            methods: z.array(z.string()).nullish(),
            required: z.boolean(),
          }),
          avatar: z.string().nullish(),
          createdAt: z.string(),
          deletedAt: z.string().nullish(),
          info: z.object({}).partial(),
          name: z.string(),
          pushTokens: z.array(z.string()).nullish(),
          userID: z.string(),
        }),
        kind: z.string(),
        lang: z.string().nullish(),
      }),
    }),
  },
  {
    method: "patch",
    path: "/api/users/:userID",
    alias: "usersPatchUserByID",
    description: `Update user&#x27;s data`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Request data`,
        type: "Body",
        schema: usersGetProfileMe_Body,
      },
      {
        name: "userID",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z.object({
      context: z.string().nullish(),
      data: z.object({
        item: z.object({
          applID: z.string(),
          avatar: z.string().nullish(),
          createdAt: z.string(),
          deletedAt: z.string().nullish(),
          email: z.string().nullish(),
          info: z.object({}).partial(),
          inusID: z.number().nullish(),
          name: z.string(),
          userID: z.string(),
        }),
        kind: z.string(),
        lang: z.string().nullish(),
      }),
    }),
  },
  {
    method: "get",
    path: "/api/users/profile/:userID",
    alias: "usersGetProfileMe",
    description: `Get user profile`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Request data`,
        type: "Body",
        schema: usersGetProfileMe_Body,
      },
      {
        name: "userID",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z.object({
      context: z.string().nullish(),
      data: z.object({
        item: z.object({
          applID: z.string(),
          avatar: z.string().nullish(),
          createdAt: z.string(),
          deletedAt: z.string().nullish(),
          email: z.string().nullish(),
          info: z.object({}).partial(),
          inusID: z.number().nullish(),
          name: z.string(),
          userID: z.string(),
        }),
        kind: z.string(),
        lang: z.string().nullish(),
      }),
    }),
  },
]);

export const api = new Zodios(endpoints);

export function createApiClient(baseUrl: string, options?: ZodiosOptions) {
  return new Zodios(baseUrl, endpoints, options);
}
